import { useEffect, useState, useSyncExternalStore } from 'react';
import { log } from '../../app/logger';
import { applyUpdate, shouldPromptUpdate, subscribeSwUpdate } from '../../app/swUpdate';
import { subscribeWakeLock, wakeLockStatus } from '../../app/wakeLock';
import type { WakeLockStatus } from '../../app/wakeLock';
import { status as writerStatus, subscribeWriterStatus } from '../../app/writerLock';
import type { WriterStatus } from '../../app/writerLock';
import { chooseStripNotice } from './stripNotices';
import type { MatchRow } from '../../types';

/**
 * B-8 — the takeover's **single-slot notification strip** (design spec §2.4).
 *
 * One slot, fixed height, filled or empty — the height belongs to the band in `capture.css`, so
 * nothing this strip shows or stops showing ever moves the chrome. The decisions (which kinds
 * are live, the arbitration, the copy) are `stripNotices.ts`, pure and Node-proven; this
 * component is the subscription adapter:
 *
 * - writer lock and wake lock live outside React, so they are **subscribed**, never polled —
 *   a subscription fires exactly where the underlying state is assigned (baseline §4.3/§4.4:
 *   "subscribe, don't poll");
 * - the SW update rides `useSyncExternalStore` over `subscribeSwUpdate` (the shape A's
 *   UpdateBanner set). **This strip is B's first consumer of `notifyUpdateReady`** — the B-0
 *   flag closes here: a waiting worker now reaches the operator's eyes, as an offer at the
 *   lowest strip priority, and nothing ever forces the reload (OBS-4).
 * - the nudge predicates (`overrunNudgeDue`) are render-time reads of the wall clock the rAF
 *   minute clock supplies — never timers (iOS may never fire a timer armed before the phone
 *   went into a pocket; the predicate is simply true on the next painted frame).
 */
export interface NotificationStripBProps {
  readonly match: MatchRow;
  /** Wall clock from the rAF minute clock, refreshed at each minute boundary. */
  readonly now: number;
  /**
   * B-19a — the user's LOCK-1 minute (§2.4's *"lock nudge minutes"*), already parsed by
   * `ui-b/lockNudge.ts` and read by the host inside its load gate. Threaded as a prop rather than
   * read here so that the strip's FIRST evaluation is already against the user's number: a
   * resume at 18′ with the setting at 20′ must never paint the offer and then withdraw it.
   */
  readonly lockNudgeMin: number;
  /**
   * B-10 — the lock nudge's action: opens the lock review surface. The offer itself is the
   * engine's `lockNudgeDue` (via `chooseStripNotice`); this callback is the ONLY thing the tap
   * does — nothing here locks anything ("the app never auto-locks").
   */
  readonly onLockNudge: () => void;
  /**
   * B-35/R-7 — the finished coach card is on screen. The one input this strip takes that is about
   * the *middle band* rather than about the match, and it withholds exactly one kind
   * (`stripNotices.cardShowing`). Threaded as a prop for the same reason `lockNudgeMin` is: the
   * strip's FIRST evaluation must already be against the true value, and this component subscribes
   * to things that live outside React — a card being on screen is not one of them.
   */
  readonly cardShowing: boolean;
}

export function NotificationStripB({
  match,
  now,
  lockNudgeMin,
  onLockNudge,
  cardShowing,
}: NotificationStripBProps) {
  const [writer, setWriter] = useState<WriterStatus>(() => writerStatus());
  const [wake, setWake] = useState<WakeLockStatus>(() => wakeLockStatus());
  const updateReady = useSyncExternalStore(subscribeSwUpdate, shouldPromptUpdate, () => false);

  useEffect(() => subscribeWriterStatus(setWriter), []);
  useEffect(() => subscribeWakeLock(setWake), []);

  const notice = chooseStripNotice({
    match,
    now,
    lockNudgeMin,
    writerHoldsLock: writer.holdsLock,
    wakeDegraded: wake.degraded,
    wakeSupported: wake.supported,
    updateReady,
    cardShowing,
  });

  return (
    <div
      className="capb-strip"
      data-slot="notification"
      data-testid="capb-strip"
      data-notice={notice?.kind ?? 'none'}
    >
      {/* An empty `text` renders no chip — a notice may be carried wholly by its action (the
          lock offer), because a ~50 px chip beside the whistle and a button clips any copy. */}
      {notice !== null && notice.text !== '' && (
        <span className="capb-strip__text" role={notice.role} data-testid="capb-strip-text">
          {notice.text}
        </span>
      )}
      {notice?.actionLabel !== undefined && (
        <button
          type="button"
          className="capb-strip__action"
          data-testid="capb-strip-action"
          onClick={() => {
            // The action is the notice's own, dispatched by kind — one button, two offers.
            if (notice.kind === 'lockNudge') {
              onLockNudge();
              return;
            }
            // OBS-4 — the user chose it; `applyUpdate` posts SKIP_WAITING and reloads. It throws
            // `EnvironmentError` if nothing waits (a race with a second tab) — logged, not fatal:
            // the strip re-picks next render and the notice simply clears.
            applyUpdate().catch((cause: unknown) => {
              log.exception('ui-b/capture', 'OBS-4: the offered update could not be applied', cause);
            });
          }}
        >
          {notice.actionLabel}
        </button>
      )}
    </div>
  );
}
